import type { ReactNode } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { fetchSchool } from '@/lib/api'
import { TeamAvatar } from '@/components/ui/TeamAvatar'
import { RankBadge } from '@/components/ui/RankBadge'
import { Skeleton } from '@/components/ui/Skeleton'
import type { SchoolTeam, SchoolTeamGame } from '@/types/api'
import clsx from 'clsx'

// Frontend sport param is men/women; the API returns mens_soccer/womens_soccer.
function sportPath(sport: string): string {
  return sport.startsWith('women') ? 'women' : 'men'
}

export default function SchoolPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data, loading, error } = useApi(
    () => slug ? fetchSchool(slug) : Promise.reject('No slug'),
    [slug],
  )

  if (error) return (
    <div className="card p-8 text-center">
      <p className="text-red-400 font-medium">School not found</p>
    </div>
  )

  if (loading || !data) return (
    <div className="space-y-6">
      <Skeleton variant="card" className="h-32" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} variant="card" className="h-64" />)}
      </div>
    </div>
  )

  const { school, teams } = data

  return (
    <div className="space-y-8">
      <div className="card-elevated p-6 sm:p-8">
        <div className="flex items-center gap-5">
          <TeamAvatar name={school.name} logoUrl={school.logo_url} colorSeed={school.conference_name ?? undefined} size="lg" />
          <div>
            <h1 className="text-3xl font-bold text-white">{school.name}</h1>
            {school.conference_name && <p className="text-slate-400 mt-1">{school.conference_name}</p>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
              {school.athletic_department_url && (
                <a href={school.athletic_department_url} target="_blank" rel="noopener noreferrer" className="text-sm text-accent hover:underline">
                  Athletics website ↗
                </a>
              )}
              {school.organization_url && (
                <a href={school.organization_url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-accent hover:underline">
                  School website ↗
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {teams.map((team) => <TeamPanel key={team.sport} team={team} />)}
      </div>
    </div>
  )
}

function TeamPanel({ team }: { team: SchoolTeam }) {
  const sport = sportPath(team.sport)
  const hasRecord = team.record.wins + team.record.losses + team.record.draws > 0
  // Teams on another hub (e.g. volleyball viewed from the soccer hub) link out
  // to that hub's domain; same-hub teams stay in-app.
  const base = team.external ? `${team.hub_origin ?? ''}/${sport}` : `/${sport}`

  return (
    <section className="card p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="section-header flex-1">
          <h2 className="text-lg font-bold text-slate-100">{team.sport_label}</h2>
          <TeamLink base={base} slug={team.slug} external={team.external} className="text-sm font-medium text-accent hover:text-accent-light transition-colors">
            {team.external
              ? `View on ${team.hub_name ?? 'the other hub'} ↗`
              : 'Team page →'}
          </TeamLink>
        </div>
        {team.ranking && (
          <div className="text-right shrink-0">
            <RankBadge rank={team.ranking.rank} prevRank={team.ranking.prev_rank} size="sm" />
            <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wide">Coaches Poll</div>
          </div>
        )}
      </div>
      {hasRecord && (
        <p className="text-sm text-slate-400 mb-3">
          Record: <span className="font-mono text-slate-200" style={{ fontVariantNumeric: 'tabular-nums' }}>{team.record.display}</span>
        </p>
      )}
      {team.camp_url && (
        <a href={team.camp_url} target="_blank" rel="noopener noreferrer" className="text-sm text-accent mb-3 inline-block hover:underline">
          Camps ↗
        </a>
      )}
      <div className="space-y-1.5">
        {team.recent_games.map((g) => <GameRow key={g.id} game={g} base={base} external={team.external} />)}
        {team.recent_games.length === 0 && (
          <p className="text-sm text-slate-500">No games recorded.</p>
        )}
      </div>
    </section>
  )
}

// Links to a team page that may live on another hub. Internal targets use the
// SPA router; external ones are plain anchors that open the other hub.
function TeamLink({ base, slug, external, className, children }: {
  base: string
  slug: string
  external: boolean
  className?: string
  children: ReactNode
}) {
  const href = `${base}/teams/${slug}`
  if (external) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
  }
  return <Link to={href} className={className}>{children}</Link>
}

function GameRow({ game, base, external }: { game: SchoolTeamGame; base: string; external: boolean }) {
  const isFinal = game.status?.toLowerCase() === 'final'
  const won = isFinal && game.team_score != null && game.opponent_score != null && game.team_score > game.opponent_score
  const lost = isFinal && game.team_score != null && game.opponent_score != null && game.team_score < game.opponent_score

  return (
    <div className="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-pitch-800/40 transition-colors text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs text-slate-500 w-6">{game.home ? 'vs' : '@'}</span>
        {game.opponent_slug ? (
          <TeamLink base={base} slug={game.opponent_slug} external={external} className="truncate hover:text-accent transition-colors">
            {game.opponent_name}
          </TeamLink>
        ) : (
          <span className="truncate text-slate-300">{game.opponent_name}</span>
        )}
      </div>
      <span className="font-mono text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {won && <span className="text-zone-safe font-bold mr-1">W</span>}
        {lost && <span className="text-zone-out font-bold mr-1">L</span>}
        {!won && !lost && isFinal && <span className="text-slate-400 mr-1">D</span>}
        {isFinal
          ? <span className="text-slate-300">{game.team_score}-{game.opponent_score}</span>
          : <span className={clsx('text-slate-500')}>{game.status}</span>}
      </span>
    </div>
  )
}
